import { useRef } from "react";
import {
  CheckCircle2,
  Download,
  FileArchive,
  Loader2,
  Paperclip,
  Sparkles,
  Wand2,
} from "lucide-react";

import { toast } from "@/components/ui/sonner";
import { Tooltip, TooltipContent, TooltipTrigger } from "@/components/ui/tooltip";
import { useAppStore, type PendingMode } from "@/store/app";

function pendingToken() {
  return Math.random().toString(36).slice(2);
}
import {
  documentDownloadUrl,
  documentsZipUrl,
  useGenerateSynthetic,
  useGetSession,
  useUploadDocument,
} from "@/hooks/api";
import { ensureSession } from "@/lib/session";
import {
  ALLOWED_FIRST_KINDS,
  KINDS,
  APPEAL_TYPE_REQUIRED_KINDS,
  SYNTHESIZABLE_KINDS,
  UPLOADABLE_KINDS,
  isEdi,
  kindMeta,
} from "@/lib/document-kinds";
import type { DocumentInfo, DocumentKind } from "@/api/types";


export function DocumentRail() {
  const selectedDocId = useAppStore((s) => s.selectedDocId);
  const selectDoc = useAppStore((s) => s.selectDoc);
  const sessionId = useAppStore((s) => s.sessionId);
  const openAppealTypeDialog = useAppStore((s) => s.openAppealTypeDialog);
  const setView = useAppStore((s) => s.setView);

  // User-initiated row click: select the doc *and* flip the main panel
  // back from the letter preview to the document viewer so the click
  // actually does something visible.
  function openDoc(id: string) {
    selectDoc(id);
    setView("analysis");
  }

  const { data: session } = useGetSession(sessionId);
  const documents = session?.documents ?? [];
  const syntheticLocked = session?.synthetic_locked ?? false;
  const hasAppealType = (session?.appeal_types?.length ?? 0) > 0;

  const { generateSynthetic } = useGenerateSynthetic();
  const { uploadDocument } = useUploadDocument();
  const pendingEntries = useAppStore((s) => s.pendingEntries);
  const addPending = useAppStore((s) => s.addPending);
  const removePending = useAppStore((s) => s.removePending);

  const docs = [...documents].sort((a, b) => a.uploaded_at.localeCompare(b.uploaded_at));
  const isLetterhead = (k: DocumentKind) => k === "letterhead";
  const ediDocs = docs.filter((d) => isEdi(d.kind));
  const letterheadDocs = docs.filter((d) => isLetterhead(d.kind));
  const supportingDocs = docs.filter((d) => !isEdi(d.kind) && !isLetterhead(d.kind));
  const hasFirstDoc = docs.length > 0 || pendingEntries.length > 0;
  const presentKinds = new Set(docs.map((d) => d.kind));
  const ediKinds = KINDS.filter((k) => k.group === "EDI");
  const letterheadKinds = KINDS.filter((k) => k.group === "Letterhead");
  const supportingKinds = KINDS.filter((k) => k.group === "Supporting");

  // Pending rows come from the store — populated by handlers here AND
  // by EmptyState (so its "Try sample" sequence of synth requests
  // shows progress here too after the immediate layout transition).
  // Filter out kinds that already landed so a just-finished entry
  // doesn't briefly duplicate as both pending row and real doc row.
  const pendingRows = pendingEntries.filter((p) => !presentKinds.has(p.kind));
  const pendingKindSet = new Set(pendingRows.map((p) => p.kind));
  const pendingEdi = pendingRows.filter((p) => isEdi(p.kind));
  const pendingLetterhead = pendingRows.filter((p) => isLetterhead(p.kind));
  const pendingSupporting = pendingRows.filter((p) => !isEdi(p.kind) && !isLetterhead(p.kind));
  const unavailableKinds = new Set<DocumentKind>([...presentKinds, ...pendingKindSet]);

  function isUploadAllowed(kind: DocumentKind): boolean {
    if (!UPLOADABLE_KINDS.includes(kind)) return false;
    if (syntheticLocked) return false;
    if (unavailableKinds.has(kind)) return false;
    if (!hasFirstDoc && !ALLOWED_FIRST_KINDS.includes(kind)) return false;
    return true;
  }

  function isSynthesizeAllowed(kind: DocumentKind): boolean {
    if (!SYNTHESIZABLE_KINDS.includes(kind)) return false;
    if (unavailableKinds.has(kind)) return false;
    if (!hasFirstDoc && !ALLOWED_FIRST_KINDS.includes(kind)) return false;
    return true;
  }

  // Synchronous re-entry guard. react-query's `isPending` flips to
  // true only after the next render, so two fast clicks would both
  // pass the disabled check and fire parallel requests; a ref lets
  // us bail before the await chain even starts.
  const inFlightRef = useRef<Set<string>>(new Set());
  const isInFlight = (kind: DocumentKind, mode: PendingMode) =>
    inFlightRef.current.has(`${mode}:${kind}`);
  const markInFlight = (kind: DocumentKind, mode: PendingMode) =>
    inFlightRef.current.add(`${mode}:${kind}`);
  const clearInFlight = (kind: DocumentKind, mode: PendingMode) =>
    inFlightRef.current.delete(`${mode}:${kind}`);

  async function onSynthesize(kind: DocumentKind) {
    // The appeal-type picker only fires when the kind actually encodes
    // the denial reason (835 / EOB). 837 / UB04 / MR are appeal-type-
    // independent — synth them directly and the backend uses a
    // default clinical appeal type for seed content.
    if (APPEAL_TYPE_REQUIRED_KINDS.includes(kind) && !hasAppealType) {
      openAppealTypeDialog({ mode: "single", kind });
      return;
    }
    if (isInFlight(kind, "synthesize")) return;
    markInFlight(kind, "synthesize");
    const t = pendingToken();
    addPending({ token: t, kind, mode: "synthesize" });
    try {
      const sid = await ensureSession();
      const parsed = await generateSynthetic({ sessionId: sid, kind });
      if (!selectedDocId) selectDoc(parsed.info.id);
      toast.success(`Generated ${parsed.info.filename}`);
    } catch (e) {
      const msg = e instanceof Error ? e.message : String(e);
      toast.error(`Generation failed: ${msg}`);
    } finally {
      clearInFlight(kind, "synthesize");
      removePending(t);
    }
  }

  async function onUpload(kind: DocumentKind) {
    if (isInFlight(kind, "upload")) return;
    const input = document.createElement("input");
    input.type = "file";
    input.accept = isEdi(kind)
      ? ".edi,.txt,.dat"
      : kind === "letterhead"
        ? ".png,.jpg,.jpeg"
        : ".pdf";
    input.onchange = async () => {
      const file = input.files?.[0];
      if (!file) return;
      if (isInFlight(kind, "upload")) return;
      markInFlight(kind, "upload");
      const t = pendingToken();
      addPending({ token: t, kind, mode: "upload" });
      try {
        const sid = await ensureSession();
        const parsed = await uploadDocument({ sessionId: sid, kind, file });
        if (!selectedDocId) selectDoc(parsed.info.id);
        toast.success(`Uploaded ${file.name}`);
      } catch (e) {
        const msg = e instanceof Error ? e.message : String(e);
        toast.error(`Upload failed: ${msg}`);
      } finally {
        clearInFlight(kind, "upload");
        removePending(t);
      }
    };
    input.click();
  }

  const hasDocs = docs.length > 0;
  const zipHref = sessionId && hasDocs ? documentsZipUrl(sessionId) : null;

  return (
    <div className="h-full overflow-y-auto p-3 space-y-3">
      {zipHref && (
        <div className="flex items-center justify-end">
          <a
            href={zipHref}
            download
            title="Download all documents as a zip archive"
            className="inline-flex items-center gap-1.5 text-[12px] font-medium text-slate-600 hover:text-slate-900 px-2 py-1 rounded-md hover:bg-slate-100"
          >
            <FileArchive className="w-3.5 h-3.5" />
            Download all (.zip)
          </a>
        </div>
      )}
      <Group title="EDI">
        {ediDocs.map((d) => (
          <DocRow
            key={d.id}
            doc={d}
            sessionId={sessionId}
            selected={selectedDocId === d.id}
            onSelect={() => openDoc(d.id)}
          />
        ))}
        {pendingEdi.map((p) => (
          <PendingDocRow key={`pending-${p.kind}`} kind={p.kind} mode={p.mode} />
        ))}
        {ediKinds
          .filter((k) => !presentKinds.has(k.kind) && !pendingKindSet.has(k.kind))
          .map((k) => (
            <AddableDocRow
              key={`add-${k.kind}`}
              kind={k.kind}
              uploadOk={isUploadAllowed(k.kind)}
              synthOk={isSynthesizeAllowed(k.kind)}
              onUpload={() => onUpload(k.kind)}
              onSynthesize={() => onSynthesize(k.kind)}
            />
          ))}
      </Group>

      <Group title="Letterhead">
        {letterheadDocs.map((d) => (
          <DocRow
            key={d.id}
            doc={d}
            sessionId={sessionId}
            selected={selectedDocId === d.id}
            onSelect={() => openDoc(d.id)}
          />
        ))}
        {pendingLetterhead.map((p) => (
          <PendingDocRow key={`pending-${p.kind}`} kind={p.kind} mode={p.mode} />
        ))}
        {letterheadKinds
          .filter((k) => !presentKinds.has(k.kind) && !pendingKindSet.has(k.kind))
          .map((k) => (
            <AddableDocRow
              key={`add-${k.kind}`}
              kind={k.kind}
              uploadOk={isUploadAllowed(k.kind)}
              synthOk={isSynthesizeAllowed(k.kind)}
              onUpload={() => onUpload(k.kind)}
              onSynthesize={() => onSynthesize(k.kind)}
            />
          ))}
      </Group>

      <Group title="Supporting">
        {supportingDocs.map((d) => (
          <DocRow
            key={d.id}
            doc={d}
            sessionId={sessionId}
            selected={selectedDocId === d.id}
            onSelect={() => openDoc(d.id)}
          />
        ))}
        {pendingSupporting.map((p) => (
          <PendingDocRow key={`pending-${p.kind}`} kind={p.kind} mode={p.mode} />
        ))}
        {supportingKinds
          .filter((k) => !presentKinds.has(k.kind) && !pendingKindSet.has(k.kind))
          .map((k) => (
            <AddableDocRow
              key={`add-${k.kind}`}
              kind={k.kind}
              uploadOk={isUploadAllowed(k.kind)}
              synthOk={isSynthesizeAllowed(k.kind)}
              onUpload={() => onUpload(k.kind)}
              onSynthesize={() => onSynthesize(k.kind)}
            />
          ))}
      </Group>
    </div>
  );
}

function Group({ title, children }: { title: string; children: React.ReactNode }) {
  return (
    <div>
      <div className="px-1 text-[11px] font-medium uppercase tracking-wider text-muted-foreground mb-1">
        {title}
      </div>
      <div className="space-y-0.5">{children}</div>
    </div>
  );
}

function DocRow({
  doc,
  sessionId,
  selected,
  onSelect,
}: {
  doc: DocumentInfo;
  sessionId: string | null;
  selected: boolean;
  onSelect: () => void;
}) {
  const downloadHref = sessionId ? documentDownloadUrl(sessionId, doc.id) : null;
  const meta = kindMeta(doc.kind);
  return (
    <div
      className={
        "relative w-full flex items-start gap-2 pl-3 pr-1 py-1.5 rounded-md " +
        (selected ? "bg-slate-100" : "hover:bg-slate-50")
      }
    >
      {selected && (
        <span className="absolute left-0 top-1 bottom-1 w-[2px] bg-foreground rounded" />
      )}
      <button
        type="button"
        onClick={onSelect}
        className="flex-1 min-w-0 flex items-start gap-2 cursor-pointer text-left bg-transparent"
      >
        <span className="mt-0.5 shrink-0">
          <DocStatusIcon parsed={doc.parsed ?? false} />
        </span>
        <span className="flex-1 min-w-0">
          <span className="flex items-center gap-1.5 min-w-0">
            <span className="text-[13px] truncate font-medium min-w-0">{doc.filename}</span>
            {doc.is_synthetic && (
              <Tooltip>
                <TooltipTrigger asChild>
                  <Sparkles
                    className="w-3 h-3 text-violet-500 shrink-0"
                    aria-label="synthetic"
                  />
                </TooltipTrigger>
                <TooltipContent side="top" className="max-w-[220px] text-xs">
                  Synthetic document — generated by the demo to match the chosen
                  scenario, not a real upload.
                </TooltipContent>
              </Tooltip>
            )}
          </span>
          <span className="block text-[11px] italic text-muted-foreground/80 leading-snug">
            {meta.description}
          </span>
        </span>
      </button>
      {downloadHref && (
        <a
          href={downloadHref}
          download={doc.filename}
          title="Download"
          aria-label={`Download ${doc.filename}`}
          onClick={(e) => e.stopPropagation()}
          className="shrink-0 p-1.5 rounded-md text-slate-600 hover:bg-slate-100 transition-colors"
        >
          <Download className="w-3.5 h-3.5" />
        </a>
      )}
    </div>
  );
}

function DocStatusIcon({ parsed }: { parsed: boolean }) {
  if (parsed) return <CheckCircle2 className="w-4 h-4 text-emerald-600" />;
  return <Loader2 className="w-4 h-4 text-muted-foreground animate-spin" />;
}

function AddableDocRow({
  kind,
  uploadOk,
  synthOk,
  onUpload,
  onSynthesize,
}: {
  kind: DocumentKind;
  uploadOk: boolean;
  synthOk: boolean;
  onUpload: () => void;
  onSynthesize: () => void;
}) {
  const meta = kindMeta(kind);
  return (
    <div className="group relative w-full flex items-start gap-2 pl-3 pr-1 py-1.5 rounded-md hover:bg-slate-50">
      <span className="mt-0.5 inline-block w-4 h-4 rounded-full border border-dashed border-slate-300" />
      <div className="flex-1 min-w-0">
        <div className="text-[13px] font-medium text-muted-foreground truncate">{meta.label}</div>
        <div className="text-[11px] italic text-muted-foreground/80 leading-snug">
          {meta.description}
        </div>
      </div>
      <div className="flex items-center gap-0.5 shrink-0">
        <IconAction
          icon={<Paperclip className="w-3.5 h-3.5" />}
          label="Upload file"
          disabled={!uploadOk}
          onClick={onUpload}
        />
        <IconAction
          icon={<Wand2 className="w-3.5 h-3.5" />}
          label="Synthesize"
          disabled={!synthOk}
          onClick={onSynthesize}
          accent="violet"
        />
      </div>
    </div>
  );
}

function IconAction({
  icon,
  label,
  disabled,
  onClick,
  accent,
}: {
  icon: React.ReactNode;
  label: string;
  disabled: boolean;
  onClick: () => void;
  accent?: "violet";
}) {
  const color = accent === "violet" ? "text-violet-600 hover:bg-violet-50" : "text-slate-600 hover:bg-slate-100";
  return (
    <button
      type="button"
      aria-label={label}
      title={label}
      disabled={disabled}
      onClick={onClick}
      className={
        "p-1.5 rounded-md transition-colors disabled:opacity-30 disabled:cursor-not-allowed disabled:hover:bg-transparent " +
        color
      }
    >
      {icon}
    </button>
  );
}

function PendingDocRow({ kind, mode }: { kind: DocumentKind; mode: PendingMode }) {
  const meta = kindMeta(kind);
  const verb = mode === "synthesize" ? "Synthesizing" : "Uploading";
  return (
    <div
      aria-busy="true"
      className="relative w-full flex items-center gap-2 pl-3 pr-2 py-1.5 rounded-md text-left animate-pulse"
    >
      <Loader2 className="w-4 h-4 text-violet-500 animate-spin" />
      <span className="text-[13px] flex-1 truncate font-medium text-muted-foreground">
        {verb} {meta.defaultFilename}…
      </span>
      {mode === "synthesize" && <Sparkles className="w-3 h-3 text-violet-400" />}
    </div>
  );
}
